import {
  chartBounds,
  formatEatDateTime,
  formatVitalValue,
  isCriticalInterpretation,
  VITAL_SIGNS,
  VITAL_SIGNS_IN_ORDER,
  vitalChartSeries,
  type ChartPoint,
  type VitalKey,
} from '@wodi/domain';
import type { VitalSignSetView } from '@wodi/shared';
import { Select, VITAL_FLAG_WORDS } from '@wodi/ui';
import { useState } from 'react';

import styles from './nursing.module.css';

// A simple line chart of one vital over the stay (docs/plans/phase2-ward-rounds-nursing.md
// §4.5, §5): ink line on paper, tokens only. A normal value is a hollow point; a flagged
// value is a filled point; a critical value is a filled point in `danger`, the one clinical
// danger hue (DESIGN.md §2). The table below the chart carries every value in words, so
// nothing rests on the shape alone. No reference ranges are drawn (plan W10).

const WIDTH = 640;
const HEIGHT = 200;
const PAD = { top: 16, right: 16, bottom: 28, left: 56 };

export interface PlottedPoint {
  x: number;
  y: number;
  point: ChartPoint;
  shape: 'normal' | 'flagged' | 'critical';
}

/** Where each point goes in the chart's box, oldest on the left. */
export function plot(
  key: VitalKey,
  points: readonly ChartPoint[],
): { plotted: PlottedPoint[]; low: number; high: number } {
  const { low, high } = chartBounds(
    key,
    points.map((p) => p.scaled),
  );
  const times = points.map((p) => Date.parse(p.at));
  const first = Math.min(...times);
  const last = Math.max(...times);
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const plotted = points.map((point, i) => {
    const t = times[i] ?? first;
    const x =
      last === first ? PAD.left + innerW / 2 : PAD.left + ((t - first) / (last - first)) * innerW;
    const y = PAD.top + innerH - ((point.scaled - low) / (high - low)) * innerH;
    const shape: PlottedPoint['shape'] = isCriticalInterpretation(point.interpretation)
      ? 'critical'
      : point.interpretation === 'normal' || point.interpretation === 'no_range'
        ? 'normal'
        : 'flagged';
    return { x, y, point, shape };
  });
  return { plotted, low, high };
}

function Chart({ vital, points }: { vital: VitalKey; points: readonly ChartPoint[] }) {
  const definition = VITAL_SIGNS[vital];
  const { plotted, low, high } = plot(vital, points);
  const latest = points.at(-1);
  const summary =
    latest === undefined
      ? ''
      : `${definition.label} over the stay: ${points.length === 1 ? '1 reading' : `${String(points.length)} readings`}, latest ${formatVitalValue(vital, latest.scaled)} ${definition.unitDisplay}.`;
  return (
    <figure className={styles.chart}>
      <svg
        viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
        className={styles.chartSvg}
        role="img"
        aria-label={summary}
      >
        <line
          className={styles.axis}
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={HEIGHT - PAD.bottom}
          y2={HEIGHT - PAD.bottom}
        />
        <line
          className={styles.axis}
          x1={PAD.left}
          x2={PAD.left}
          y1={PAD.top}
          y2={HEIGHT - PAD.bottom}
        />
        <text className={styles.axisLabel} x={PAD.left - 8} y={PAD.top + 4} textAnchor="end">
          {formatVitalValue(vital, high)}
        </text>
        <text
          className={styles.axisLabel}
          x={PAD.left - 8}
          y={HEIGHT - PAD.bottom}
          textAnchor="end"
        >
          {formatVitalValue(vital, low)}
        </text>
        {plotted.length > 1 ? (
          <polyline
            className={styles.line}
            points={plotted.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
          />
        ) : null}
        {plotted.map((p) => (
          <circle
            key={p.point.setId}
            className={styles.point}
            data-shape={p.shape}
            cx={p.x}
            cy={p.y}
            r={p.shape === 'normal' ? 4 : 5}
          />
        ))}
      </svg>
      <figcaption className={styles.caption}>
        {`${definition.label} (${definition.unitDisplay}). Hollow: in range or no range set. Filled: flagged. Red: critical.`}
      </figcaption>
    </figure>
  );
}

/** The chart with a picker of the vitals that have values in this stay. */
export function VitalsChart({ sets }: { sets: readonly VitalSignSetView[] }) {
  const series = vitalChartSeries(
    sets.map((s) => ({
      id: s.id,
      effectiveAt: s.effectiveAt,
      status: s.status,
      observations: s.observations,
    })),
  );
  const available = VITAL_SIGNS_IN_ORDER.filter((d) => series.has(d.key));
  const [chosen, setChosen] = useState<VitalKey | null>(null);
  if (available.length === 0) {
    return <p className={styles.meta}>No vitals recorded in this stay yet.</p>;
  }
  const vital =
    chosen !== null && series.has(chosen) ? chosen : (available[0]?.key ?? 'body_temperature');
  return (
    <div className={styles.chartBlock}>
      <Select
        label="Vital shown"
        width="sm"
        options={available.map((d) => ({ id: d.key, label: d.label }))}
        value={vital}
        onChange={(v) => {
          setChosen(v as VitalKey);
        }}
      />
      <Chart vital={vital} points={series.get(vital) ?? []} />
    </div>
  );
}

/** Words for a flagged value, as the triage screen uses them. */
export function flagWords(interpretation: ChartPoint['interpretation']): string | null {
  return interpretation === 'normal' || interpretation === 'no_range'
    ? null
    : VITAL_FLAG_WORDS[interpretation];
}

export function when(iso: string): string {
  return formatEatDateTime(new Date(iso));
}
